
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  AlertTriangle, 
  Clock, 
  Phone, 
  Calendar, 
  FileText, 
  User, 
  Heart,
  Thermometer,
  Activity
} from "lucide-react";
import { TriageData, TriageOutcome } from "@/types/triage";
import { format } from "date-fns";
import { TriageExplanationPanel } from "./TriageExplanationPanel";
import { CollapsibleCard } from "@/components/ui/collapsible-card";

interface TriageResultProps {
  result: TriageOutcome;
  triageData: TriageData;
  triageId?: string; // Optional triage ID from the database
  patientId?: string; // Optional patient ID
}

export function TriageResult({ result, triageData, triageId, patientId }: TriageResultProps) {
  // Log the IDs for debugging
  console.log('[TriageResult] Rendering with IDs:', { triageId, patientId });
  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'immediate': return 'bg-red-500 text-white';
      case 'urgent': return 'bg-orange-500 text-white';
      case 'standard': return 'bg-yellow-500 text-black';
      case 'non-urgent': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getRecommendationIcon = (urgency: string) => {
    switch (urgency) {
      case 'immediate': return <Phone className="w-6 h-6" />;
      case 'urgent': return <AlertTriangle className="w-6 h-6" />;
      case 'standard': return <Calendar className="w-6 h-6" />;
      case 'non-urgent': return <Clock className="w-6 h-6" />;
      default: return <Activity className="w-6 h-6" />;
    }
  };

  const downloadSummary = () => {
    const summary = `
TRIAGE ASSESSMENT SUMMARY
Generated: ${format(new Date(), 'PPpp')}

URGENCY LEVEL: ${result.urgency.toUpperCase()}
MANCHESTER TRIAGE CATEGORY: ${result.manchesterCategory}

SYMPTOMS:
${triageData.symptoms.map(s => `• ${s}`).join('\n')}

SEVERITY: ${triageData.severity}
DURATION: ${triageData.duration}
ONSET: ${triageData.onset}

VITAL SIGNS:
${triageData.vitals.temperature ? `Temperature: ${triageData.vitals.temperature}°F` : ''}
${triageData.vitals.heartRate ? `Heart Rate: ${triageData.vitals.heartRate} bpm` : ''}
${triageData.vitals.bloodPressure ? `Blood Pressure: ${triageData.vitals.bloodPressure}` : ''}

MEDICAL HISTORY:
${triageData.medicalHistory.length > 0 ? triageData.medicalHistory.map(h => `• ${h}`).join('\n') : 'None reported'}

CURRENT MEDICATIONS:
${triageData.medications.length > 0 ? triageData.medications.map(m => `• ${m}`).join('\n') : 'None reported'}

ALLERGIES:
${triageData.allergies.length > 0 ? triageData.allergies.map(a => `• ${a}`).join('\n') : 'None reported'}

RECOMMENDATION:
${result.recommendation}

RED FLAGS IDENTIFIED:
${result.redFlags.length > 0 ? result.redFlags.map(f => `• ${f}`).join('\n') : 'None identified'}

CLINICAL NOTES:
${result.clinicalNotes}

---
This assessment is for informational purposes only and does not replace professional medical advice.
    `;

    const blob = new Blob([summary], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `triage-summary-${format(new Date(), 'yyyy-MM-dd-HHmm')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Assessment Result */}
      <CollapsibleCard
        title={
          <div className="flex items-center gap-3">
            {getRecommendationIcon(result.urgency)}
            <span>Assessment Complete</span>
          </div>
        }
        description="Based on the information provided, here's your triage assessment"
        defaultOpen={true}
      >
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Badge className={`text-lg px-4 py-2 ${getUrgencyColor(result.urgency)}`}>
              {result.urgency.toUpperCase()} URGENCY
            </Badge>
            <div className="text-sm">
              Manchester Triage Category: <strong>{result.manchesterCategory}</strong>
            </div>
          </div>

          <Separator />

          <div>
            <h3 className="text-lg font-semibold mb-2">Recommendation</h3>
            <p>{result.recommendation}</p>
          </div>

          {result.redFlags.length > 0 && (
            <>
              <Separator />
              <div>
                <h4 className="font-medium text-amber-700 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Important Considerations / Red Flags
                </h4>
                <ul className="list-disc list-inside text-sm space-y-1 text-amber-800 bg-amber-50 p-3 rounded-md">
                  {result.redFlags.map((flag, index) => (
                    <li key={index}>{flag}</li>
                  ))}
                </ul>
              </div>
            </>
          )}

          {/* TriageExplanationPanel for symptoms, clinical notes, and AI explanation was here. 
              It seems the original structure had these inside the main card content.
              The provided view_line_range shows a more complex structure than the initial codebase_search for TriageResult.
              For now, I will replicate the structure from the initial codebase_search which had TriageExplanationPanel (singular) 
              and other sections like Patient/Vitals summary and Chief Complaint directly inside the main card content.
              If the more complex structure with multiple explanation panels is desired, it can be adjusted later.
          */}
          {/* Detailed Symptom Analysis */}
          <div className="mt-6">
            <h4 className="font-bold text-gray-900 mb-2">Detailed Symptom Analysis</h4>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <TriageExplanationPanel
                triageData={triageData}
                triageOutcome={result}
                triageId={triageId}
                patientId={patientId}
                panelId="symptoms-panel"
                symptomsWithSources={result.symptomsWithSources ||
                  (result.processedSymptoms ?
                    result.processedSymptoms.map(s => ({ name: s, source: 'explicit' })) :
                    triageData.symptoms.map(s => ({ name: s, source: 'explicit' })))
                }
              />
            </div>
          </div>

          {/* Clinical Notes */}
          {result.clinicalNotes && (
            <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-bold text-gray-900 mb-2">Clinical Notes</h4>
              <p className="text-gray-700">{result.clinicalNotes}</p>
            </div>
          )}

          {/* AI Triage Explanation */}
          {result.explanation && (
            <div className="mt-6">
                <h4 className="font-bold text-gray-900 mb-2">AI Triage Explanation</h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <TriageExplanationPanel
                    triageData={triageData}
                    triageOutcome={result} // Pass result, panel might use result.explanation for this panelId
                    triageId={triageId}
                    patientId={patientId}
                    panelId="ai-explanation-panel"
                />
                </div>
            </div>
          )}
          
          <Separator />

          {/* Patient and Vitals Summary - This section was in the original CardContent based on earlier views of TriageResult */} 
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <User className="w-5 h-5" />
                Patient Summary
              </h4>
              <div className="text-sm space-y-1">
                {triageData.age && <div>Age: {triageData.age}</div>}
                {triageData.gender && <div>Gender: {triageData.gender}</div>}
                {triageData.isPregnant && <div className="text-amber-700 font-medium">Patient is pregnant</div>}
                {triageData.smokingStatus && <div>Smoking Status: {triageData.smokingStatus}</div>}
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Vital Signs Recorded
              </h4>
              <div className="text-sm space-y-1">
                {triageData.vitals.temperature && (
                  <div><Thermometer className="w-4 h-4 inline mr-1" />Temperature: {triageData.vitals.temperature}°F</div>
                )}
                {triageData.vitals.heartRate && (
                  <div><Heart className="w-4 h-4 inline mr-1" />Heart Rate: {triageData.vitals.heartRate} bpm</div>
                )}
                {triageData.vitals.bloodPressure && (
                  <div><Activity className="w-4 h-4 inline mr-1" />Blood Pressure: {triageData.vitals.bloodPressure}</div>
                )}
                {!triageData.vitals.temperature && !triageData.vitals.heartRate && !triageData.vitals.bloodPressure && (
                  <div className="text-gray-500">No vital signs recorded</div>
                )}
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Chief Complaint</h4>
            <div className="space-y-2">
              <div>
                <span className="font-medium">Symptoms:</span>{' '}
                {triageData.symptoms.join(', ')}
              </div>
              <div>
                <span className="font-medium">Severity:</span> {triageData.severity}
              </div>
              <div>
                <span className="font-medium">Duration:</span> {triageData.duration}
              </div>
              <div>
                <span className="font-medium">Onset:</span> {triageData.onset}
              </div>
              {triageData.additionalInfo && (
                <div>
                  <span className="font-medium">Additional Information:</span>{' '}
                  {triageData.additionalInfo}
                </div>
              )}
            </div>
          </div>

          {(triageData.medicalHistory.length > 0 || triageData.medications.length > 0 || triageData.allergies.length > 0) && (
            <>
              <Separator />
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <h4 className="font-medium mb-2">Medical History</h4>
                  {triageData.medicalHistory.length > 0 ? (
                    <ul className="text-sm space-y-1">
                      {triageData.medicalHistory.map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-gray-500 text-sm">None reported</div>
                  )}
                </div>

                <div>
                  <h4 className="font-medium mb-2">Current Medications</h4>
                  {triageData.medications.length > 0 ? (
                    <ul className="text-sm space-y-1">
                      {triageData.medications.map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-gray-500 text-sm">None reported</div>
                  )}
                </div>

                <div>
                  <h4 className="font-medium mb-2">Allergies</h4>
                  {triageData.allergies.length > 0 ? (
                    <ul className="text-sm space-y-1">
                      {triageData.allergies.map((item, index) => (
                        <li key={index} className="text-red-600">• {item}</li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-gray-500 text-sm">None reported</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </CollapsibleCard>

      {/* Summary for Healthcare Provider */}
      <CollapsibleCard
        title={
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            <span>Healthcare Provider Summary</span>
          </div>
        }
        description="This summary will be shared with your healthcare provider"
        defaultOpen={false} // Keep this collapsed by default
      >
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <User className="w-4 h-4" />
                Patient Demographics
              </h4>
              <div className="space-y-1 text-sm">
                <div>Age: {triageData.age}</div>
                <div>Gender: {triageData.gender}</div>
                {triageData.isPregnant && <div className="text-pink-600">Currently pregnant</div>}
                {triageData.smokingStatus && <div>Smoking: {triageData.smokingStatus}</div>}
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Vital Signs
              </h4>
              <div className="space-y-1 text-sm">
                {triageData.vitals.temperature && (
                  <div className="flex items-center gap-2">
                    <Thermometer className="w-3 h-3" />
                    Temperature: {triageData.vitals.temperature}°F
                  </div>
                )}
                {triageData.vitals.heartRate && (
                  <div className="flex items-center gap-2">
                    <Heart className="w-3 h-3" />
                    Heart Rate: {triageData.vitals.heartRate} bpm
                  </div>
                )}
                {triageData.vitals.bloodPressure && (
                  <div>Blood Pressure: {triageData.vitals.bloodPressure}</div>
                )}
                {!triageData.vitals.temperature && !triageData.vitals.heartRate && !triageData.vitals.bloodPressure && (
                  <div className="text-gray-500">No vital signs recorded</div>
                )}
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Chief Complaint</h4>
            <div className="space-y-2">
              <div>
                <span className="font-medium">Symptoms:</span>{' '}
                {triageData.symptoms.join(', ')}
              </div>
              <div>
                <span className="font-medium">Severity:</span> {triageData.severity}
              </div>
              <div>
                <span className="font-medium">Duration:</span> {triageData.duration}
              </div>
              <div>
                <span className="font-medium">Onset:</span> {triageData.onset}
              </div>
              {triageData.additionalInfo && (
                <div>
                  <span className="font-medium">Additional Information:</span>{' '}
                  {triageData.additionalInfo}
                </div>
              )}
            </div>
          </div>

          {(triageData.medicalHistory.length > 0 || triageData.medications.length > 0 || triageData.allergies.length > 0) && (
            <>
              <Separator />
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <h4 className="font-medium mb-2">Medical History</h4>
                  {triageData.medicalHistory.length > 0 ? (
                    <ul className="text-sm space-y-1">
                      {triageData.medicalHistory.map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-gray-500 text-sm">None reported</div>
                  )}
                </div>

                <div>
                  <h4 className="font-medium mb-2">Current Medications</h4>
                  {triageData.medications.length > 0 ? (
                    <ul className="text-sm space-y-1">
                      {triageData.medications.map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-gray-500 text-sm">None reported</div>
                  )}
                </div>

                <div>
                  <h4 className="font-medium mb-2">Allergies</h4>
                  {triageData.allergies.length > 0 ? (
                    <ul className="text-sm space-y-1">
                      {triageData.allergies.map((item, index) => (
                        <li key={index} className="text-red-600">• {item}</li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-gray-500 text-sm">None reported</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </CollapsibleCard>

      {/* Actions */}
      <div className="flex gap-4 justify-between">
        <Button variant="outline" onClick={downloadSummary}>
          <FileText className="w-4 h-4 mr-2" />
          Download Summary
        </Button>
        
        <div className="flex gap-2">
          {result.urgency !== 'immediate' && (
            <Button>
              <Calendar className="w-4 h-4 mr-2" />
              Book Appointment
            </Button>
          )}
          
          {result.urgency === 'immediate' && (
            <Button className="bg-red-600 hover:bg-red-700">
              <Phone className="w-4 h-4 mr-2" />
              Call Emergency Services
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
